import { Link, useParams } from "react-router-dom";
import TopNav from "@/components/feature/TopNav";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import NewsCard from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";
import type { NewsItem } from "@/types/content";
import { useSeo } from "@/hooks/useSeo";

const allNews = news as NewsItem[];

export default function Article() {
  const { id } = useParams();
  const item = allNews.find((entry) => entry.id === id);

  const related = item
    ? allNews.filter((entry) => entry.categorySlug === item.categorySlug && entry.id !== item.id).slice(0, 3)
    : [];
  const fallbackRelated = item && related.length < 3
    ? allNews.filter((entry) => entry.id !== item.id && !related.includes(entry)).slice(0, 3 - related.length)
    : [];
  const recommendations = [...related, ...fallbackRelated];

  const jsonLd = item
    ? [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: item.title,
          description: item.excerpt,
          author: { "@type": "Organization", name: item.author },
          articleSection: item.category,
          keywords: item.tags.join(","),
          inLanguage: "zh-CN",
          datePublished: "2026-09-12",
          timeRequired: "PT3M",
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "/" },
            { "@type": "ListItem", position: 2, name: item.category, item: "/topics" },
            { "@type": "ListItem", position: 3, name: item.title, item: `/article/${item.id}` },
          ],
        },
      ]
    : undefined;

  useSeo({
    title: item ? `${item.title} | 黑料网爆` : "内容未找到 | 黑料网爆",
    description: item
      ? `${item.excerpt} 黑料网爆持续跟进${item.category}相关的最新讨论与时间线。`
      : "你访问的内容可能已被更新或移除，请返回首页查看最新的热点爆料。",
    keywords: item ? `${item.tags.join(",")},黑料网爆,${item.category}` : "黑料网爆",
    canonicalPath: `/article/${id}`,
    ogType: "article",
    jsonLd,
  });

  if (!item) {
    return (
      <>
        <TopNav />
        <main className="w-full px-4 md:px-8 py-24">
          <div className="mx-auto max-w-lg text-center">
            <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
              <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">内容未找到</h1>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              这条爆料可能已被更新或合并到其他话题中。回到首页，看看此刻全网正在聊什么。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopNav />
      <main>
        <article className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-8 md:py-12">
            <nav aria-label="面包屑导航">
              <ol className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </span>
                  <Link to="/topics" className="hover:text-primary-600 transition-colors cursor-pointer">
                    {item.category}
                  </Link>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                    <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-foreground-700 line-clamp-1 max-w-[240px]">{item.title}</span>
                </li>
              </ol>
            </nav>

            <div className="mt-6 max-w-3xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-background-200 px-3 py-1 text-[11px] font-medium text-foreground-700">
                  {item.category}
                </span>
                {item.isFresh && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                    <span className="relative w-1.5 h-1.5 rounded-full bg-background-50 live-dot" />
                    刚刚发生
                  </span>
                )}
                {item.isLive && (
                  <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-900">
                    实时跟进
                  </span>
                )}
                {item.isEvening && (
                  <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                    晚间热搜
                  </span>
                )}
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold leading-tight tracking-tight text-foreground-950">
                {item.title}
              </h1>

              <p className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                  </span>
                  {item.author}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  {item.time}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                    <i className="ri-fire-line" aria-hidden="true"></i>
                  </span>
                  热度 {item.heat}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                  </span>
                  {item.views}
                </span>
              </p>
            </div>
          </div>

          <div className="w-full px-4 md:px-8">
            <div className="w-full h-64 md:h-[420px] overflow-hidden rounded-lg bg-background-200">
              <img
                src={item.image}
                alt={`${item.title} 配图`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 lg:gap-10">
              <div className="min-w-0">
                <p className="text-base md:text-lg leading-8 text-foreground-800 font-medium">
                  {item.excerpt}
                </p>

                <div className="mt-6 space-y-5">
                  {item.content.map((paragraph) => (
                    <p key={paragraph.slice(0, 16)} className="text-sm md:text-base leading-8 text-foreground-700">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-background-300/80 bg-background-100 px-3 py-1.5 text-xs text-foreground-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-lg border border-background-300/60 bg-background-100 p-5">
                  <p className="text-sm text-foreground-600">想看更多同类话题，可以回到分类专题继续浏览。</p>
                  <Link
                    to="/topics"
                    className="inline-flex items-center gap-2 rounded-md border border-background-400/70 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    返回分类专题
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" aria-hidden="true"></i>
                    </span>
                  </Link>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-700">
                      <i className="ri-list-check-2" aria-hidden="true"></i>
                    </span>
                    本文要点
                  </h2>
                  <ul className="mt-4 space-y-3">
                    {item.points.map((point) => (
                      <li key={point} className="flex items-start gap-2.5 text-sm leading-6 text-foreground-700">
                        <span className="mt-1 w-1.5 h-1.5 shrink-0 rounded-full bg-accent-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-300/60 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
                  <div className="mt-2">
                    {recommendations.map((entry) => (
                      <NewsCard key={entry.id} item={entry} variant="compact" />
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-background-300/60 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">内容说明</h2>
                  <p className="mt-3 text-xs leading-6 text-foreground-600">
                    本文整理自公开讨论与网友反馈，以「全网正在讨论」的视角呈现，仅供娱乐资讯参考。若内容存在偏差，欢迎通过联系邮箱反馈，我们会尽快核对与更新。
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </article>

        <section className="w-full bg-background-100">
          <div className="w-full px-4 md:px-8 py-12 md:py-16">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                {item.category} · 更多内容
              </h2>
              <Link
                to="/topics"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </span>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recommendations.map((entry) => (
                <NewsCard key={`grid-${entry.id}`} item={entry} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}